import React, { useEffect, useState } from 'react';
import api from '../api';
import './Dialer.css';

const formatStatus = (status) => String(status || 'NEW').replace(/_/g, ' ');

const getStatusBadgeClass = (status) => {
  const s = String(status || 'NEW').toLowerCase().replace(/_/g, '-');
  return `badge badge-${s}`;
};

export default function Dialer({ auth }) {
  const [lead, setLead] = useState(null);
  const [leads, setLeads] = useState([]);
  const [number, setNumber] = useState('');
  const [agentPhone, setAgentPhone] = useState(() => localStorage.getItem('agentPhone') || '');
  const [callInfo, setCallInfo] = useState(null);
  const [dispositions, setDispositions] = useState([]);
  const [dispositionId, setDispositionId] = useState('');
  const [notes, setNotes] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [dialMode, setDialMode] = useState('queue'); // 'queue' | 'custom'
  const [autoCreateLead, setAutoCreateLead] = useState(true);
  const [customDestination, setCustomDestination] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [foreignCall, setForeignCall] = useState(false);

  useEffect(() => {
    // If "Call" was clicked in My Leads, pre-select that lead
    const pre = sessionStorage.getItem('dialLead');
    if (pre) {
      try {
        setLead(JSON.parse(pre));
      } catch (e) {
        console.error(e);
      }
      sessionStorage.removeItem('dialLead');
    }
    api.get('/api/calls/dispositions').then((r) => setDispositions(r.data || [])).catch(() => {});
    api.get('/api/leads?size=1000').then((r) => setLeads(r.data?.content || r.data || [])).catch(() => {});
  }, []);

  const safeLeads = Array.isArray(leads) ? leads : [];

  useEffect(() => {
    if (lead?.leadId) {
      api.get(`/api/calls/lead/${lead.leadId}`).then((r) => setHistory(r.data || [])).catch(() => {});
      const p = lead.phone || '';
      setNumber(p);
      // Automatically toggle international route if number is non-Indian
      if (p && !p.startsWith('+91')) {
        setForeignCall(true);
      } else {
        setForeignCall(false);
      }
      setCallInfo(null);
      setDispositionId('');
      setNotes('');
      setMsg('');
      setErr('');
    }
  }, [lead]);

  const handleCountryCodeChange = (code) => {
    setCountryCode(code);
    if (code !== '+91') {
      setForeignCall(true);
    } else {
      setForeignCall(false);
    }
  };

  const pickLead = async (id) => {
    if (!id) return setLead(null);
    try {
      const { data } = await api.get(`/api/leads/${id}`);
      setLead(data);
    } catch (e) {
      console.error(e);
    }
  };

  const dial = async () => {
    setErr('');
    setMsg('');
    
    let targetNumber = dialMode === 'custom' ? (countryCode + customDestination) : number;
    let targetLeadId = lead?.leadId || null;

    if (dialMode === 'queue') {
      if (!lead || !number) {
        setErr('Please select a lead and enter a valid phone number.');
        return;
      }
    } else {
      if (!customDestination) {
        setErr('Please enter a destination phone number.');
        return;
      }
      if (autoCreateLead) {
        try {
          const res = await api.post('/api/leads', {
            firstName: 'Custom',
            lastName: 'Call',
            phone: targetNumber,
            status: 'NEW',
            source: 'WEB'
          });
          targetLeadId = res.data.leadId;
          setLead(res.data);
        } catch (e) {
          setErr('Failed to auto-create lead.');
          return;
        }
      }
    }

    const myPhone = agentPhone.trim();
    if (!/^\+[1-9]\d{7,14}$/.test(myPhone)) {
      setErr('Enter YOUR agent phone number in E.164 format (e.g. +919876543210) for callback purposes.');
      return;
    }
    localStorage.setItem('agentPhone', myPhone);

    try {
      const { data } = await api.post('/api/calls/dial', {
        leadId: targetLeadId,
        to: targetNumber,
        agentPhone: myPhone,
        foreignCall: foreignCall,
      });
      setCallInfo(data);
      setMsg(
        data.dryRun
          ? `Dry-run call logged (${data.plivoAccount || 'Account'} not configured on backend).`
          : `Call initiated via Plivo [${data.plivoAccount || (foreignCall ? 'US' : 'India')}]. Caller ID: ${data.callerIdNumber || (foreignCall ? '+1 561 973 6118' : '+91 8031707808')}. (Call ID: ${data.providerCallId}).`
      );
    } catch (e) {
      setErr(e.response?.data?.error || 'Call initiation failed.');
    }
  };

  const saveOutcome = async () => {
    setErr('');
    setMsg('');
    if (!callInfo?.callId) {
      setErr('Place a call first before logging an outcome.');
      return;
    }

    try {
      await api.post('/api/calls/outcome', {
        callId: callInfo.callId,
        dispositionId: dispositionId ? Number(dispositionId) : null,
        notes,
      });
      setMsg('Call disposition and notes saved successfully.');
      if (lead?.leadId) {
        const h = await api.get(`/api/calls/lead/${lead.leadId}`);
        setHistory(h.data || []);
      }
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save call outcome.');
    }
  };

  return (
    <div className="dialer-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Voice Dialer Station</h1>
          <p className="page-subtitle">Initiate outbound calls with dual-route Plivo dialing (Domestic 🇮🇳 &amp; International 🇺🇸).</p>
        </div>
      </div>

      {err && <div className="dialer-error">{err}</div>}
      {msg && <div className="dialer-success">{msg}</div>}

      <div className="dialer-grid">
        {/* Left Column: Outbound Station */}
        <div className="dialer-card">
          <div className="dialer-card-body">
            <h2 className="card-section-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span>Outbound Station</span>
              </div>
              <div className="dialer-tabs">
                <button className={dialMode === 'queue' ? 'active' : ''} onClick={() => setDialMode('queue')}>
                  Lead Queue
                </button>
                <button className={dialMode === 'custom' ? 'active' : ''} onClick={() => setDialMode('custom')}>
                  Custom Direct Call
                </button>
              </div>
            </h2>

            {dialMode === 'queue' ? (
              <div className="field-group">
                <label>Select Target Lead</label>
                <select value={lead?.leadId || ''} onChange={(e) => pickLead(e.target.value)}>
                  <option value="">Choose a lead from queue...</option>
                  {safeLeads.map((l) => (
                    <option key={l.leadId} value={l.leadId}>
                      {l.firstName} {l.lastName} — {l.phone} {l.company ? `(${l.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className="field-group">
                  <label>Direct Destination Phone Number (E.164)</label>
                  <div className="phone-input-group">
                    <select
                      value={countryCode}
                      onChange={(e) => handleCountryCodeChange(e.target.value)}
                      className="country-select"
                    >
                      <option value="+91">🇮🇳 IN +91</option>
                      <option value="+1">🇺🇸 US/CA +1</option>
                      <option value="+44">🇬🇧 UK +44</option>
                      <option value="+61">🇦🇺 AU +61</option>
                      <option value="+971">🇦🇪 UAE +971</option>
                      <option value="+65">🇸🇬 SG +65</option>
                      <option value="+49">🇩🇪 DE +49</option>
                    </select>
                    <input
                      type="text"
                      value={customDestination}
                      onChange={(e) => setCustomDestination(e.target.value)}
                      placeholder="9876543210"
                    />
                  </div>
                </div>
                <div className="field-group checkbox-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', textTransform: 'none', fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={autoCreateLead}
                      onChange={(e) => setAutoCreateLead(e.target.checked)}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    Auto-create and log this custom direct call as a new CRM lead record
                  </label>
                </div>
              </>
            )}

            {/* International Call Route Box */}
            <div className={`foreign-call-box ${foreignCall ? 'active' : ''}`}>
              <label className="foreign-call-toggle">
                <input
                  type="checkbox"
                  checked={foreignCall}
                  onChange={(e) => setForeignCall(e.target.checked)}
                />
                <span>Call via International Route (US Plivo Account)</span>
              </label>

              <div className={`caller-id-banner ${foreignCall ? 'us' : 'india'}`}>
                <span>
                  {foreignCall
                    ? '🇺🇸 Active Route: US International (+1 561 973 6118)'
                    : '🇮🇳 Active Route: India Domestic (+91 8031707808)'}
                </span>
                <span className="caller-id-pill">
                  {foreignCall ? '+1 561 973 6118' : '+91 8031707808'}
                </span>
              </div>
            </div>

            <div className="field-row">
              <div className="field-group">
                <label>Your Agent Phone (E.164)</label>
                <input
                  type="text"
                  value={agentPhone}
                  onChange={(e) => setAgentPhone(e.target.value)}
                  placeholder="+919876543210"
                />
              </div>

              <div className="field-group">
                <label>Number to Dial (E.164)</label>
                <input
                  type="text"
                  value={dialMode === 'custom' ? (customDestination ? countryCode + customDestination : '') : number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="+919876543210"
                  readOnly={dialMode === 'custom'}
                  style={dialMode === 'custom' ? { backgroundColor: '#f3f4f6', color: '#6b7280' } : {}}
                />
              </div>
            </div>

            <button className="btn-place-call" onClick={dial} disabled={dialMode === 'queue' && !lead}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span>Place Call Now</span>
            </button>

            {callInfo && (
              <div className="active-call-banner">
                <div className="pulse-indicator"></div>
                <div>
                  <strong>Active Session:</strong> {callInfo.providerCallId}
                  <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '2px' }}>
                    Status: {formatStatus(callInfo.status)} &bull; Route: {callInfo.plivoAccount || (foreignCall ? 'US Account (+1 561 973 6118)' : 'India Account (+91 8031707808)')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Lead Dossier & Outcome */}
        <div className="dialer-side-column">
          {lead && (
            <div className="dialer-card">
              <div className="dialer-card-body">
                <h2 className="card-section-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <span>Lead Dossier</span>
                </h2>

                <div className="lead-summary">
                  <div className="lead-header">
                    <h3>{lead.firstName} {lead.lastName}</h3>
                    <span className={getStatusBadgeClass(lead.status)}>{formatStatus(lead.status)}</span>
                  </div>

                  <div className="lead-details-grid">
                    <div>
                      <span className="detail-label">Company</span>
                      <span className="detail-val">{lead.company || '—'}</span>
                    </div>
                    <div>
                      <span className="detail-label">Phone</span>
                      <span className="detail-val">{lead.phone || '—'}</span>
                    </div>
                    <div>
                      <span className="detail-label">Email</span>
                      <span className="detail-val">{lead.email || '—'}</span>
                    </div>
                    <div>
                      <span className="detail-label">Source</span>
                      <span className="detail-val">{lead.source || '—'}</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.25rem' }}>
                  <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                    Call Disposition &amp; Notes
                  </h3>

                  <div className="field-group">
                    <label>Call Outcome</label>
                    <select value={dispositionId} onChange={(e) => setDispositionId(e.target.value)}>
                      <option value="">Select an outcome disposition...</option>
                      {dispositions.map((d) => (
                        <option key={d.dispositionId} value={d.dispositionId}>
                          {d.name} {d.description ? `— ${d.description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field-group">
                    <label>Agent Notes</label>
                    <textarea
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add conversation notes, next steps, follow-up dates..."
                    ></textarea>
                  </div>

                  <button className="btn-save-outcome" onClick={saveOutcome} disabled={!callInfo}>
                    Save Call Log
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Call History */}
          {history.length > 0 && (
            <div className="dialer-card">
              <div className="dialer-card-body">
                <h2 className="card-section-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>Interaction Log ({history.length})</span>
                </h2>

                <div className="call-history-list">
                  {history.map((c) => (
                    <div key={c.callId} className="call-history-item">
                      <div className="call-history-header">
                        <strong>{c.disposition?.name || 'Initiated'}</strong>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {c.startTime ? new Date(c.startTime).toLocaleString() : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        From: {c.fromNumber || '—'} &bull; To: {c.toNumber}
                      </div>
                      {c.notes && <div className="call-history-notes">"{c.notes}"</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
